import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { NonIdealState, AnchorButton, Button, Intent } from '@blueprintjs/core'

import { APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN } from '@config/app.config'

import './styles.scss'

/**
 * Shown instead of the editor when it couldn't load the user because of a
 * network failure or a server error (FE-1475). Those say nothing about the
 * user or the item, so the user gets a retry rather than the not-found page.
 *
 * Rendered before the editor's Provider, so it takes the app model as a prop.
 * Retrying reloads the page, which runs the whole startup again.
 *
 * index.html lays the editor out 1024px wide. On a phone that would leave this
 * screen off to the side, and most of these failures come from iPhones. While
 * it's shown it's the only thing on the page, so it's fitted to the screen.
 * The editor's viewport comes back when the page reloads, or when an in-app
 * navigation renders the editor again.
 */
const StartupError = ({ app }) => {
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]')
    const editorViewport = meta?.getAttribute('content')
    meta?.setAttribute('content', 'width=device-width, initial-scale=1.0')
    return () => {
      if (editorViewport) {
        meta.setAttribute('content', editorViewport)
      }
    }
  }, [])

  return (
    <div className="startup-error" role="alert">
      <NonIdealState
        icon="error"
        title={app.formatMessage('common.loadComponent')}
        description={app.formatMessage('common.loadComponent.description')}
        action={
          <div className="startup-error__actions">
            <AnchorButton href={APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN}>
              {app.formatMessage('common.returnToDashboard')}
            </AnchorButton>
            <AnchorButton
              href={`${APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN}/contact/`}
              target="_blank"
            >
              {app.formatMessage('common.getInTouch')}
            </AnchorButton>
            <Button
              intent={Intent.PRIMARY}
              icon="refresh"
              onClick={() => window.location.reload()}
            >
              {app.formatMessage('common.reconnect')}
            </Button>
          </div>
        }
      />
    </div>
  )
}

StartupError.propTypes = {
  app: PropTypes.object.isRequired,
}

export default StartupError
